import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  Hidden,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
} from '@material-ui/core'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import {
  BarChart,
  Bell,
  Book,
  BookOpen,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clipboard,
  Film,
  Flag,
  Image,
  Layers,
  MessageSquare,
  Music,
  Tool,
  Type,
  Users,
  Video
} from 'react-feather';
import { Link as RouterLink, useLocation } from 'react-router-dom'
import useUser from 'src/hooks/useUser'

import NavItem from './NavItem'

const groupedItems = [
  {
    group: 'General',
    items: [
      {
        href: '/app/dashboard',
        icon: BarChart,
        title: 'Dashboard',
      },
      {
        href: '/app/languages',
        icon: Book,
        title: 'Languages',
      },
      {
        href: '/app/banners',
        icon: Image,
        title: 'Banners',
      },
    ],
  },
  {
    group: 'Content',
    items: [
      {
        href: '/app/courses',
        icon: BookOpen,
        title: 'Courses',
      },
      {
        href: '/app/chapters',
        icon: Layers,
        title: 'Chapters',
      },
      {
        href: '/app/lessons',
        icon: BookOpen,
        title: 'Lessons',
      },
      {
        href: '/app/practice-list',
        icon: Clipboard,
        title: 'Practice List',
      },
      {
        href: '/app/practice-item',
        icon: CheckSquare,
        title: 'Practice Item',
      },
    ],
  },
  {
    group: 'Media',
    items: [
      {
        href: '/app/media/images',
        icon: Image,
        title: 'Images',
      },
      {
        href: '/app/media/audio',
        icon: Music,
        title: 'Audio',
      },
      {
        href: '/app/media/videos',
        icon: Film,
        title: 'Videos',
      },
    ],
  },
  {
    group: '4L Explore',
    items: [

      {
        href: '/app/4l-videos',
        icon: Video,
        title: '4L Videos',
      },
      {
        href: '/app/4l-categories',
        icon: Layers,
        title: '4L Categories',
      },
      {
        href: '/app/entertainmentVocabularies',
        icon: Type,
        title: 'Vocabularies',
      },
    ],
  },
  {
    group: 'Users',
    items: [
      {
        href: '/app/customers',
        icon: Users,
        title: 'Students',
      },
      // {
      //   href: '/app/dashboard',
      //   icon: Users,
      //   title: 'Editors',
      // },
      // {
      //   href: '/app/dashboard',
      //   icon: Users,
      //   title: 'Mentors',
      // },
      // {
      //   href: '/app/dashboard',
      //   icon: Users,
      //   title: 'Project Manager',
      // },
      // {
      //   href: '/app/dashboard',
      //   icon: Users,
      //   title: 'Admins',
      // },
    ],
  },
  {
    group: 'Student Feedback',
    items: [
      {
        href: '/app/reports',
        icon: Flag,
        title: 'Flags',
      },
      {
        href: '/app/feedbacks',
        icon: MessageSquare,
        title: 'Rating & Review',
      },
    ],
  },
  {
    group: 'Notification',
    items: [
      {
        href: '/app/notifications',
        icon: Bell,
        title: 'Notification',
      },
    ],
  },
  {
    group: 'System',
    items: [
      {
        href: '/app/servicelogs',
        icon: Tool,
        title: 'Service Logs',
      },
    ],
  },
  // {
  //   href: '/app/products',
  //   icon: ShoppingBagIcon,
  //   title: 'Products'
  // },
  // {
  //   href: '/app/account',
  //   icon: UserIcon,
  //   title: 'Account'
  // },
  // {
  //   href: '/app/settings',
  //   icon: SettingsIcon,
  //   title: 'Settings'
  // },
  // {
  //   href: '/login',
  //   icon: LockIcon,
  //   title: 'Login'
  // },
  // {
  //   href: '/register',
  //   icon: UserPlusIcon,
  //   title: 'Register'
  // },
  // {
  //   href: '/404',
  //   icon: AlertCircleIcon,
  //   title: 'Error'
  // }
]

const DashboardSidebar = ({ onMobileClose, openMobile }) => {
  const { data: user } = useUser()
  const location = useLocation()
  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (group) => {
    setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose()
    }
  }, [location.pathname, onMobileClose, openMobile]);

  // styles into a variable
  const scrollableBoxStyles = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    paddingBottom: "20px",

    // WebKit browsers
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'primary.main',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'primary.dark',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'background.default',
    },

    // Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: 'primary.main background.default',
  }

  const content = (
    <Box
      sx={scrollableBoxStyles}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          p: 2,
        }}
      >
        <Avatar
          component={RouterLink}
          src={user?.image ?? '/static/images/avatars/avatar_5.png'}
          sx={{
            cursor: 'pointer',
            width: 64,
            height: 64,
          }}
          to="/app/account"
        />
        <Typography color="textPrimary" variant="h5">
          {user?.firstName ? user?.firstName : user?.username}
          {user?.lastName && user?.lastName}
        </Typography>
        <Typography color="textSecondary" variant="body2">
          {user?.role}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        {groupedItems?.map((group) => (
          <List
            key={group?.group}
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: group?.items?.some((item) =>
                    location?.pathname?.includes(item?.href)
                  )
                    ? "primary.main"
                    : "background.paper",
                  color: group?.items?.some((item) =>
                    location?.pathname?.includes(item?.href)
                  )
                    ? 'primary.contrastText' : 'text.secondary',
                  textTransform: "uppercase",
                  borderRadius: 1,
                  px: 0,
                  marginBottom: "2px"
                }}
              >
                <ListItemButton
                  onClick={() => toggleGroup(group?.group)}
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: "5px"
                  }}
                >
                  <ListItemText
                    primary={group?.group}
                    primaryTypographyProps={{ style: { fontWeight: 500 } }}
                  />
                  {openGroups[group?.group] ? (
                    <ChevronDown size={18} strokeWidth={2.5} />
                  ) : (
                    <ChevronRight size={18} strokeWidth={2.5} />
                  )}
                </ListItemButton>
              </ListSubheader>
            }
          >
            <Collapse in={openGroups[group?.group]} timeout="auto" unmountOnExit>
              {group?.items?.map((nav) => (
                <NavItem
                  href={nav?.href}
                  key={nav?.title}
                  title={nav?.title}
                  icon={nav?.icon}
                />
              ))}
            </Collapse>
          </List>
        ))
        }
      </Box >
      <Box sx={{ flexGrow: 1 }} />
    </Box >
  )

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
          PaperProps={{
            sx: {
              width: 256,
            },
          }}
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: 256,
              top: 64,
              height: 'calc(100% - 64px)',
            },
          }}
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  )
}

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
}

DashboardSidebar.defaultProps = {
  onMobileClose: () => { },
  openMobile: false,
}

export default DashboardSidebar
